import {
  CommonService,
  CustomFormatCurrencyPipe,
  Exception,
  ToasterService,
  Validator,
} from '@anddone/ui-utilities';
import { Component, signal, OnInit, inject } from '@angular/core';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from '../../../../../services/payment.service';
import {
  NgbDropdownModule,
  NgbModal,
  NgbModalConfig,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { forkJoin } from 'rxjs';
import {
  RefundTypeMapEnum,
  TransactionStatusMapEnum,
} from '../../../../../enum/transaction-status-map.enum';
import { PortalNameEnum } from '../../../../../enum/transaction-portalName-map.enum';
import { MessageConstants } from '../../../../../constants/message.constant';
import { DatePipe, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Loader } from '../../../../layout/loader/loader';

@Component({
  selector: 'app-payment-details',
  imports: [
    TranslateModule,
    Loader,
    NgbDropdownModule,
    NgClass,
    NgbTooltipModule,
    DatePipe,
    CustomFormatCurrencyPipe,
    ReactiveFormsModule,
  ],
  templateUrl: './payment-details.html',
  styleUrl: './payment-details.scss',
})
export class PaymentDetails implements OnInit {
  isLoader = signal<boolean>(false);
  loggedInUserData: any = {};
  refundTransactions: any = {};
  transDetails: any;
  transactionId!: string;
  transactionOrigin!: string;
  transactionMethodClass: string | null = null;
  transactionStatus: any;
  settlementSection = false;
  processingFee: any;
  splitsPaymentDetails: any;
  settlementTotal = 0;
  currentIndex = 0;
  transactionTimeLineList: any = [];

  refundForm: any;
  refundFormErrors: any = {};
  confirmRefundForm: any;
  confirmRefundFormErrors: any = {};
  validator: Validator;

  isRefundEligible = false;
  isLoader_checkRefundEligiblity = signal<boolean>(true); // initial/default value is true, as it is used to display error message for refund eligibility on page load
  refundEligibleErrorMsg = '';
  partialAmountErrorFlag = false;
  isLoader_refund = signal<boolean>(false);
  errorCode: any;
  errorMessage: any;

  /**
   * Configuration object for form validation rules
   */
  config = {
    refundType: {
      required: { name: ValidationConstant.refund.refundType.name },
    },
    amount: {
      required: { name: ValidationConstant.refund.amount.name },
    },
    reason: {
      required: { name: ValidationConstant.confirmRefund.reson.name },
    },
    additionalDetails: {
      required: { name: ValidationConstant.confirmRefund.additionalDetails.name },
      minlength: {
        name: ValidationConstant.confirmRefund.additionalDetails.name,
        min: ValidationConstant.confirmRefund.additionalDetails.minLength.toString(),
      },
      maxlength: {
        name: ValidationConstant.confirmRefund.additionalDetails.name,
        max: ValidationConstant.confirmRefund.additionalDetails.maxLength.toString(),
      },
    },
  };
  private activatedRouter = inject(ActivatedRoute);
  private paymentService = inject(PaymentService);
  private commonService = inject(CommonService);
  private toasterService = inject(ToasterService);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  modalConfig = inject(NgbModalConfig);
  private formBuilder = inject(FormBuilder);
  public allyCommonService = inject(AllyCommonService);
  constructor() {
    this.validator = new Validator(this.config);
    this.modalConfig.scrollable = true;
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized
   * Initializes transaction details, user data, and refund eligibility
   * Also sets up form controls and value change subscriptions
   * @returns void
   */
  ngOnInit(): void {
    this.transactionId = this.activatedRouter.snapshot.params['id'];
    this.transactionOrigin = this.activatedRouter.snapshot.params['webOrigin'];
    this.transactionStatus = this.activatedRouter.snapshot.params['transactionStatus'];
    this.loggedInUserData = this.allyCommonService.getLoggedInData() || '';
    this.checkRefundEligibility();
    this.apiForkJoinCall();

    this.generateRefundForm();
    this.refundForm?.get('refundType')?.valueChanges.subscribe((value: string) => {
      this.handleAmountFieldState(value);
    });
    this.refundForm?.valueChanges.subscribe((_data: any) => this.onValueChanged('refundForm'));
    this.confirmRefundForm?.valueChanges.subscribe((_data: any) =>
      this.onValueChanged('confirmRefundForm')
    );
  }

  openModal(content: any) {
    this.modalService.open(content, { modalDialogClass: 'receipt-modal' });
  }

  /**
   * Generates the refund and confirm refund forms with validation rules
   * @returns void
   */
  generateRefundForm(): void {
    this.refundForm = this.formBuilder.group({
      refundType: ['net', [Validators.required]],
      amount: [{ value: '', disabled: true }, [Validators.required]],
    });
    this.confirmRefundForm = this.formBuilder.group({
      reason: ['', [Validators.required]],
      additionalDetails: [
        '',
        [
          Validators.required,
          Validators.maxLength(ValidationConstant.confirmRefund.additionalDetails.maxLength),
        ],
      ],
    });
  }

  /**
   * Resets the refund and confirm refund forms to their initial states
   * @returns void
   */
  resetForms(): void {
    this.refundForm.reset({
      refundType: 'net',
      amount: '',
    });
    this.refundForm.get('amount')?.disable();

    this.confirmRefundForm.reset({
      reason: '',
      additionalDetails: '',
    });
  }

  /**
   * Handles value changes in the refund and confirm refund forms
   * Updates error messages based on validation results
   * @param data Identifier for the form that triggered the value change
   * @returns void
   */
  onValueChanged(data?: string): void {
    if (data === 'refundForm') {
      if (!this.refundForm) {
        return;
      }
      this.refundFormErrors = this.validator.validate(this.refundForm);
    }
    if (data === 'confirmRefundForm') {
      if (!this.confirmRefundForm) {
        return;
      }
      this.confirmRefundFormErrors = this.validator.validate(this.confirmRefundForm);
    }
  }

  /**
   * Enables or disables the amount field based on the selected refund type
   * @param refundType The selected refund type
   * @returns void
   */
  handleAmountFieldState(refundType: string): void {
    const amountControl = this.refundForm.get('amount');
    if (refundType === 'partial') {
      // Enable the amount field for partial refund
      amountControl?.enable();
      amountControl?.setValidators([Validators.required]);
    } else {
      // Disable and clear the amount field for other refund types
      amountControl?.disable();
      amountControl?.clearValidators();
      amountControl?.setValue('');
    }
    amountControl?.updateValueAndValidity();
  }

  /**
   * Makes parallel API calls to fetch transaction details and timeline
   * Maps the fetched data to component properties
   * Handles loading state and error notifications
   * @returns void
   */
  apiForkJoinCall(): void {
    this.isLoader.set(true);
    const isRefunded =
      this.transactionStatus?.toLowerCase() == 'refunded' ||
      this.transactionStatus?.toLowerCase() == 'refund attempted' ||
      this.transactionStatus?.toLowerCase() == 'settled'
        ? true
        : false;
    const getTransactionDetails = this.paymentService.getTransactionDetails(
      this.transactionId,
      isRefunded
    );
    const getTimeLine = this.paymentService.getTransactionTimeLines(
      this.loggedInUserData?.id,
      this.transactionId
    );
    forkJoin([getTransactionDetails, getTimeLine]).subscribe({
      next: (results) => {
        if (results) {
          this.maptransDetails(results[0]);
          this.getTransactionTimeLine(results[1]);
        }
        this.isLoader.set(false);
      },
      error: (error) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(error);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  /**
   * Maps transaction data to component properties for display
   * Formats names, addresses, and payment methods
   * Handles settlement details if available
   * @param transactionData The transaction data fetched from the API
   * @returns void
   */
  maptransDetails(transactionData: any): void {
    this.transDetails = transactionData;
    this.transDetails.fullName = this.commonService.getFullName(
      this.transDetails?.billingContact?.name
    );

    if (this.transDetails.channelType == 'ACH') {
      this.transDetails.method = 'ACH US Direct Debit';
      this.transactionMethodClass = this.getMethodClass(this.transDetails?.method);
    } else {
      this.transDetails.method = this.transDetails.tenderInfo?.cardType;
      this.transactionMethodClass = this.getMethodClass(this.transDetails?.method);
    }
    this.commonService.getAllCountryList().subscribe({
      next: (countryRes) => {
        this.allyCommonService.setCountryList(countryRes);
        const countryListData = countryRes || [];
        const stateList = this.commonService.getMappedState(
          this.transDetails?.billingContact?.address?.country
        );
        this.transDetails.countryName = this.commonService.isValidData(
          this.transDetails?.billingContact?.address?.country
        )
          ? this.commonService.mapCountryName(
              this.transDetails?.billingContact?.address?.country,
              countryListData
            )
          : '';

        this.transDetails.stateName = this.commonService.mapStateName(
          this.transDetails?.billingContact?.address?.state,
          stateList
        );
      },
    });
    this.transDetails.shopperCompanyDetails = {};
    if (transactionData?.additionalFields) {
      this.transDetails.shopperCompanyDetails = JSON.parse(transactionData?.additionalFields);
    }
    if (
      !this.commonService.isValidData(
        this.transDetails.shopperCompanyDetails?.shoppercompanyname
      ) &&
      this.transDetails?.billingContact
    ) {
      this.transDetails.shopperCompanyDetails.shoppercompanyname =
        this.transDetails?.billingContact?.companyName;
    }
    this.refundTransactions = this.transDetails?.refundTransactions?.data || [];
    this.refundTransactions = this.transDetails?.refundTransactions?.data?.reverse();
    this.transDetails?.refundTransactions?.data?.forEach((ele: any) => {
      const key = ele.transactionStatus as keyof typeof TransactionStatusMapEnum;
      ele.transactionStatus = TransactionStatusMapEnum[key];
    });
    this.transDetails.paymentIntentId = this.transDetails?.paymentLinkId
      ? this.transDetails?.paymentLinkId
      : this.transDetails?.tokenLinkId
        ? this.transDetails?.tokenLinkId
        : '--';
    if (
      (this.transDetails?.paymentType === 'PaymentIntent' ||
        this.transDetails?.paymentType === 'PaymentLink') &&
      this.transDetails?.paymentCategory === 'MPIPD'
    ) {
      this.transDetails.portalName = 'Premium Finance Payment';
    } else {
      const key = this.transDetails?.paymentType as keyof typeof PortalNameEnum;
      this.transDetails.portalName = PortalNameEnum[key];
    }

    if (this.transDetails?.splits != null) {
      this.settlementSection = true;
      this.settlementTotal = 0;
      this.processingFee = this.transDetails?.splits.find(
        (o: any) => o?.virtualAccount.toLowerCase() === 'MerchantCommission'.toLowerCase()
      );

      this.splitsPaymentDetails = this.transDetails?.splits;
      const filterData = this.transDetails?.splits;

      const index = filterData.findIndex(
        (x: any) => x?.virtualAccount.toLowerCase() == 'AndDone_DEFAULT'.toLowerCase()
      );
      const appendLast = filterData.splice(index, 1);
      this.splitsPaymentDetails = [...this.splitsPaymentDetails, appendLast[0]];

      this.splitsPaymentDetails = this.splitsPaymentDetails
        .filter(function (obj: any) {
          return obj?.virtualAccount.toLowerCase() !== 'MerchantCommission'.toLowerCase();
        })
        .sort(function (a: any, b: any) {
          return a.chargeIndicator - b.chargeIndicator;
        })
        .map((newObj: any) => {
          if (!newObj) {
            return;
          }
          if (newObj?.virtualAccount.toLowerCase() == 'AndDone_DEFAULT'.toLowerCase()) {
            newObj.subAmt = newObj.amount - this.processingFee.amount;
          } else if (newObj?.chargeIndicator) {
            newObj.subAmt = newObj.amount - this.processingFee.amount;
          } else {
            newObj.subAmt = newObj?.amount;
          }
          this.settlementTotal += newObj?.subAmt;
          return newObj;
        });
    } else {
      this.settlementSection = false;
    }
    this.errorCode =
      this.transDetails.transactionResult?.errorCode == null
        ? this.transDetails.transactionResult?.reasonCode
        : this.transDetails.transactionResult?.errorCode;
    this.errorMessage =
      this.transDetails.transactionResult?.errorMessage == null
        ? this.transDetails.transactionResult?.additionResultData
        : this.transDetails.transactionResult?.errorMessage;

    this.errorCode = this.errorCode == null ? '--' : this.errorCode;
    this.errorMessage = this.errorMessage == null ? '--' : this.errorMessage;
  }

  /**
   * Returns the CSS class for a given payment method
   * @param methodName The name of the payment method
   * @returns The corresponding CSS class or null if not found
   */
  getMethodClass(methodName: string): string | null {
    if (!methodName) return null;
    methodName = methodName?.toUpperCase();
    switch (methodName) {
      case 'VISA':
        return 'visa';
      case 'MASTERCARD':
        return 'mastercard';
      case 'ACH US DIRECT DEBIT':
        return 'ach';
      case 'DINERS CLUB':
        return 'diners-club';
      case 'DISCOVER':
        return 'discover';
      case 'AMEX':
        return 'amex';
    }
    return null;
  }

  /**
   * Processes and formats the transaction timeline data
   * @param timeLineData The raw timeline data fetched from the API
   * @returns void
   */
  getTransactionTimeLine(res: any) {
    if (res != null) {
      this.isLoader.set(false);
      this.transactionTimeLineList = [];
      const txnStatusArr = [
        {
          id: 1,
          name: 'Authorized',
          value: 'Authorized',
          color: 'purple',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: true,
        },
        {
          id: 2,
          name: 'Settled',
          value: 'Success',
          color: 'green',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: true,
        },
        {
          id: 14,
          name: 'REFUND INITIATED',
          value: 'RefundAttempt',
          color: 'brown',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: true,
        },
        {
          id: 16,
          name: 'REFUND AUTHORIZED',
          value: 'Authorized',
          color: 'purple',
          type: 'Refund',
          displayAmt: true,
          displayDateTime: true,
        },
        {
          id: 16,
          name: 'REFUND SETTLED',
          value: 'Success',
          color: 'green',
          type: 'Refund',
          displayAmt: true,
          displayDateTime: true,
        },
        {
          id: 3,
          name: 'REFUND Denied',
          value: 'Denied',
          color: 'red',
          type: 'Refund',
          displayAmt: true,
          displayDateTime: true,
        },
        {
          id: 3,
          name: 'REFUND Failed',
          value: 'Failed',
          color: 'red',
          type: 'Refund',
          displayAmt: true,
          displayDateTime: true,
        },
        {
          id: 4,
          name: 'Chargeback',
          value: 'Chargeback',
          color: 'brown',
          type: 'Sale',
          displayAmt: true,
          displayDateTime: true,
        },
        {
          id: 5,
          name: 'Failed',
          value: 'Failed',
          color: 'red',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 6,
          name: 'Chargeback Reversed',
          value: 'ChargebackReversed',
          color: 'brown',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 7,
          name: 'Refunded Reversed',
          value: 'RefundedReversed',
          color: 'red',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 8,
          name: 'Paid',
          value: 'Paid',
          color: 'green',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 9,
          name: 'Paid Chargeback',
          value: 'PaidChargeback',
          color: 'brown',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 10,
          name: 'Paid Chargeback Reversed',
          value: 'PaidChargebackReversed',
          color: 'brown',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 11,
          name: 'Paid Refunded',
          value: 'PaidRefunded',
          color: 'red',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 12,
          name: 'Paid Refunded Reversed',
          value: 'PaidRefundedReversed',
          color: 'red',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 13,
          name: 'Returned',
          value: 'Returned',
          color: 'brown',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: false,
        },
        {
          id: 15,
          name: 'Voided',
          value: 'Cancelled',
          color: 'red',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: true,
        },
        {
          id: 2,
          name: 'Captured',
          value: 'Captured',
          color: 'blue',
          type: 'Sale',
          displayAmt: false,
          displayDateTime: true,
        },
      ];
      const selectedKey = 'transactionStatus';
      const selectedValues = ['Created', 'Approved', 'Posted'];
      const newTxnList = res.filter((item: any) => !selectedValues.includes(item[selectedKey]));
      const lastItem = newTxnList[newTxnList.length - 1];
      let settledAdded = false;

      newTxnList.forEach((item2: any) => {
        const statusItem = txnStatusArr.filter(
          (x) => x.value == item2.transactionStatus && x.type == item2.operationsType
        );
        if (statusItem?.length > 0) {
          const item = statusItem[0];
          if (item.name === 'Settled' && item.type === 'Sale' && settledAdded) {
            return;
          }
          if (item.name === 'Settled' && item.type === 'Sale') {
            settledAdded = true;
          }
          this.transactionTimeLineList.push({
            transactionStatus: item2.transactionStatus,
            status: item.name,
            color: item.color,
            datetime: item2.eventDate,
            activeTimeline: item2.transactionStatus == lastItem.transactionStatus,
            voidReason: item2.voidReason,
            chargebackReasonCode: item2.chargebackReasonCode,
            type: item2.operationsType,
            amount: item2.amount,
            operationsType: item2.operationsType,
            displayAmt: item.displayAmt,
            displayDateTime: item.displayDateTime,
          });
        }
      });
    }
  }

  /**
   * Opens a modal dialog for refund actions
   * @param type The type of refund action (e.g., 'new', 'edit')
   * @param content The template reference for the modal content
   * @returns The opened modal reference
   */
  openRefundModal(type: string, content: any): any {
    this.resetForms(); // to reset form values on modal open
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' });
  }

  /**
   * Closes the specified modal dialog
   * @param currentModalReference The reference to the modal to be closed
   * @returns void
   */
  closeModal(currentModalReference: any): void {
    currentModalReference.close();
  }

  /** Navigates back to the payments list page
   * @returns void
   */
  backToPayments(): void {
    this.router.navigate(['/ally/payments']);
  }

  /**
   * Gets the current refund transaction based on the current index
   * @returns The current refund transaction object
   */
  get currentRefundTransaction(): any {
    return this.refundTransactions[this.currentIndex];
  }

  /** Navigates to the next refund transaction in the list
   * @returns void
   */
  nextRefundTransaction(): void {
    if (this.currentIndex < this.refundTransactions.length - 1) {
      this.currentIndex++;
    }
  }

  /** Navigates to the previous refund transaction in the list
   * @returns void
   */
  prevRefundTransaction(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  /**
   * Validates input to allow only numbers, backspace, space, and decimal point
   * @param event - The keyboard event
   * @returns Boolean indicating if the character is allowed
   */
  omit_alpha_special_char_include_decimal(event: any): boolean {
    const k = event.charCode; // k = event.keyCode;  (Both can be used)
    return (k >= 48 && k <= 57) || k == 8 || k == 32 || k == 46;
  }

  /**
   * Handles input event for amount field, formats the value, and checks for errors
   * @param event - The input event
   * @returns void
   */
  onInput(event: any): void {
    const value = event.target?.value.replace(/,/g, '');
    this.amountInput(event);
    const unformattedPartialAmount = parseFloat(value);

    const formattedRemainingAmount = this.formatAmount(
      this.transDetails?.remainingAmount?.toString()
    );
    const unformattedRemainingAmount = parseFloat(formattedRemainingAmount.replace(/,/g, ''));
    if (unformattedPartialAmount > unformattedRemainingAmount) {
      this.partialAmountErrorFlag = true;
    } else {
      this.partialAmountErrorFlag = false;
    }
  }

  /** Formats the amount input field value with commas
   * @param event - The input event
   * @returns void
   */
  amountInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const formattedValue = this.formatAmount(inputElement.value);
    inputElement.value = formattedValue;
    this.refundForm.get('amount').setValue(formattedValue);
  }

  /** Formats a numeric string with commas and two decimal places
   * @param value - The numeric string to format
   * @returns The formatted string with commas
   */
  formatAmount(value: string): string {
    if (!value) return '';

    const numericValue = value.replace(/,/g, '');
    if (isNaN(Number(numericValue))) {
      return '';
    }
    const parts = numericValue.split('.');
    if (parts.length > 1) {
      parts[1] = parts[1].substring(0, 2);
    }
    const formattedValue = parts.join('.');

    return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /** Opens the confirm refund modal after validating the refund form
   * @param currentModalReference - The reference to the current modal
   * @param nextModalTemplate - The template reference for the next modal
   * @returns void
   */
  openConfirmRefundModal(currentModalReference: any, nextModalTemplate: any): void {
    // Get current form values
    const refundType = this.refundForm.get('refundType')?.value;
    const amountValue = this.refundForm.get('amount')?.value;

    // Validate based on refund type
    if (refundType === 'partial') {
      this.validator.validateAllFormFields(this.refundForm);
      this.refundFormErrors = this.validator.validate(this.refundForm);
      if (this.refundForm.invalid) {
        return;
      }

      // Validate amount format and value
      const unformattedPartialAmount = parseFloat(amountValue.replace(/,/g, ''));
      if (isNaN(unformattedPartialAmount) || unformattedPartialAmount <= 0) {
        return;
      }

      if (unformattedPartialAmount > this.transDetails?.remainingAmount) {
        return;
      }
    }
    this.closeModal(currentModalReference);
    this.modalService.open(nextModalTemplate, { ariaLabelledBy: 'modal-basic-title' });
  }

  /** Checks if the current transaction is eligible for a refund
   * @returns void
   */
  checkRefundEligibility(): void {
    this.isLoader_checkRefundEligiblity.set(true);
    this.isRefundEligible = false;
    this.paymentService
      .checkRefundEligbility(this.loggedInUserData.id, this.transactionId)
      .subscribe({
        next: (res: any) => {
          this.isLoader_checkRefundEligiblity.set(false);
          if (res.refundEligible == true) {
            this.isRefundEligible = true;
          } else {
            this.isRefundEligible = false;
            this.refundEligibleErrorMsg = MessageConstants.payment.refundEligibleErrorMsg;
          }
        },
        error: (e) => {
          this.isRefundEligible = false;
          this.isLoader_checkRefundEligiblity.set(false);
          if (e.error?.message == 'Key_PlatFormAccountTransactionCanNotBeRefunded') {
            this.refundEligibleErrorMsg = MessageConstants.payment.platformAccountErrorMsg;
          } else {
            const toastMessage = Exception.exceptionMessage(e);
            this.refundEligibleErrorMsg = toastMessage.join(', ');
          }
        },
      });
  }

  /** Confirms and initiates the refund action after validating the confirm refund form
   * @param currentModalReference - The reference to the current modal
   * @returns void
   */
  confirmRefundAction(currentModalReference: any): void {
    const additionalDetailsControl = this.confirmRefundForm.get('additionalDetails');
    if (additionalDetailsControl) {
      additionalDetailsControl.setValue(additionalDetailsControl.value?.trim() || '');
    }

    this.validator.validateAllFormFields(this.confirmRefundForm);
    this.confirmRefundFormErrors = this.validator.validate(this.confirmRefundForm);
    if (this.confirmRefundForm.invalid) {
      return;
    }

    this.isLoader_refund.set(true);
    const requestObj: any = {
      transactionId: this.transactionId,
      RefundReason: this.confirmRefundForm.value.reason,
      RefundDetail: this.confirmRefundForm.value.additionalDetails,
    };
    // Determine refund type and amount
    if (this.refundForm.value.refundType === 'net') {
      if (this.transDetails?.tenderInfo?.captureAmount === this.transDetails?.remainingAmount) {
        requestObj['refundType'] = RefundTypeMapEnum.TotalRefund;
        requestObj['refundAmount'] = this.transDetails?.tenderInfo?.captureAmount;
      } else if (
        this.transDetails?.tenderInfo?.captureAmount !== this.transDetails?.remainingAmount
      ) {
        requestObj['refundType'] = RefundTypeMapEnum.RemainingRefund;
        requestObj['refundAmount'] = this.transDetails?.remainingAmount;
      }
    } else if (this.refundForm.value.refundType === 'subtotal') {
      requestObj['refundType'] = RefundTypeMapEnum.SubTotalRefund;
      requestObj['refundAmount'] = this.transDetails?.subTotalAmount;
    } else if (this.refundForm.value.refundType === 'partial') {
      requestObj['refundType'] = RefundTypeMapEnum.PartialRefund;
      requestObj['refundAmount'] = +this.refundForm.value.amount;
    }
    this.paymentService
      .initiateRefundTransaction(requestObj, this.transDetails?.allyId, this.transactionId)
      .subscribe({
        next: () => {
          this.checkRefundEligibility();
          this.apiForkJoinCall();

          this.closeModal(currentModalReference);
          this.resetForms();

          this.isLoader_refund.set(false);
          this.toasterService.showSuccess(MessageConstants.payment.refundInitiated, 'full');
        },
        error: (e) => {
          this.isLoader_refund.set(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }
}
